import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';

import { Badge } from '@zendesk/forethought-elements';
import autoflowsBadgeIcon from 'src/assets/images/autonomous-agent-badge.svg';
import humanInTheLoopBadgeIcon from 'src/assets/images/human-in-the-loop-badge-icon.svg';
import improvedPolicyBadgeIcon from 'src/assets/images/improved-policy-badge-icon.svg';
import interactiveBadgeIcon from 'src/assets/images/interactive-badge-icon.svg';

type WorkflowBadgeType = 'autoflows' | 'interactive' | 'hitl' | 'policy';

const getBadgeConfig = (
  t: TFunction<'workflow-builder'>,
): Record<WorkflowBadgeType, { icon: string; label: string }> => ({
  autoflows: {
    icon: autoflowsBadgeIcon,
    label: t('workflowBadge.autoflows'),
  },
  hitl: {
    icon: humanInTheLoopBadgeIcon,
    label: t('workflowBadge.humanInTheLoop'),
  },
  interactive: {
    icon: interactiveBadgeIcon,
    label: t('workflowBadge.interactive'),
  },
  policy: {
    icon: improvedPolicyBadgeIcon,
    label: t('workflowBadge.policy'),
  },
});

interface WorkflowBadgeProps {
  type: WorkflowBadgeType;
}

export default function WorkflowBadge({ type }: WorkflowBadgeProps) {
  const theme = useTheme();
  const { t } = useTranslation('workflow-builder');
  const config = getBadgeConfig(t)[type];

  return (
    <Badge
      label={
        <Box
          sx={{
            alignItems: 'center',
            color:
              type === 'policy' ? theme.palette.colors.grey[700] : undefined,
            display: 'flex',
            flexDirection: 'row',
            fontSize: type === 'policy' ? '12px' : undefined,
            fontWeight: type === 'policy' ? 500 : undefined,
            gap: '4px',
            letterSpacing: type === 'policy' ? '-0.1px' : undefined,
            lineHeight: type === 'policy' ? '18px' : undefined,
            whiteSpace: 'nowrap',
          }}
        >
          <img alt='' height='14px' src={config.icon} width='14px' />
          {config.label}
        </Box>
      }
      variant='macro'
    />
  );
}
